/* eslint-disable no-console */ import { noop } from "@sail/utils";
import { useCallback, useMemo, useRef } from "react";
import { getApolloContext, useDataContext } from "../../../internal/revamp/apollo/operationContext";
import useGraphQlDataClient from "../../../internal/revamp/config/useGraphQlDataClient";
import addTypename from "../../../internal/revamp/queryUtils/addTypename";
import { reportRuntimeError } from "../../../internal/revamp/telemetry/internalReporting";
import { getOperationName } from "../../../internal/shared/utils/gql";
import useInvalidateTopLevelFields from "./useInvalidateTopLevelFields";
export default function useMutation(/**
   * The options to pass to the mutation.
   */ options) {
    const mutationDocument = options.doc;
    const transformedMutation = useMemo(()=>addTypename(mutationDocument), [
        mutationDocument
    ]);
    const { apolloLinkExecutor, memoryCache } = useGraphQlDataClient();
    const operationName = getOperationName(mutationDocument) || 'CouldNotFindMutationName';
    const expectedErrorsRef = useRef(options.expectedErrors);
    const invalidateQueries = useInvalidateTopLevelFields();
    const dataContext = useDataContext();
    const invalidateTopLevelFields = options.invalidateTopLevelFields;
    const component = options.component;
    return useCallback((callbackOptions)=>{
        const { variables } = callbackOptions;
        return new Promise((resolve)=>{
            const apolloLinkContext = getApolloContext(dataContext, {
                type: 'MUTATION',
                operationName,
                expectedErrors: expectedErrorsRef.current || []
            }, component);
            try {
                apolloLinkExecutor.execute(transformedMutation, variables, apolloLinkContext, {
                    next: (result)=>{
                        if (result.data && // We do this extra check to not write the mutation result to the cache if it's an empty object.
                        // in theory data should either be null or of the shape of mutation's result.
                        // but in practice it is often an empty object (as there are incorrect implementations of terminating links).
                        Object.keys(result.data).length > 0) {
                            memoryCache.writeMutation({
                                mutation: transformedMutation,
                                variables,
                                data: result.data
                            });
                            // Query invalidation on successful mutations when requested by consumer
                            if (typeof invalidateTopLevelFields !== 'undefined' && invalidateTopLevelFields.length > 0) {
                                invalidateQueries(invalidateTopLevelFields);
                            }
                        }
                        resolve(result);
                    },
                    complete: noop
                });
            } catch (error) {
                reportRuntimeError(apolloLinkContext.observabilityFns, error);
                resolve({
                    data: null,
                    errors: {
                        graphQlErrors: [],
                        clientErrors: [
                            error
                        ],
                        networkErrors: []
                    }
                });
            }
        });
    }, [
        component,
        dataContext,
        operationName,
        apolloLinkExecutor,
        transformedMutation,
        invalidateTopLevelFields,
        invalidateQueries,
        memoryCache
    ]);
}

//# sourceMappingURL=useMutation.js.map